import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, Megaphone, MapPin, TableProperties, Upload, ShieldCheck, Send, DollarSign } from 'lucide-react';
import { cn } from '../../lib/utils';

const SUB_NAV = [
  { to: '/mailer',           label: 'Dashboard',    icon: LayoutDashboard,  end: true },
  { to: '/mailer/campaigns', label: 'Campaigns',    icon: Megaphone },
  { to: '/mailer/spend',     label: 'Spend',        icon: DollarSign },
  { to: '/mailer/states',    label: 'States',       icon: MapPin },
  { to: '/mailer/drilldown', label: 'Drill-Down',   icon: TableProperties },
  { to: '/mailer/upload',    label: 'Upload',       icon: Upload },
  { to: '/mailer/quality',   label: 'Data Quality', icon: ShieldCheck },
];

/**
 * Shared shell for every Mailer page.
 *
 * Layout per reference: a thin orange gradient hairline runs across the very
 * top of the header. Left = ink medallion + "Mailer Dashboard" title. Right
 * (centered in the rest of the row) = a pill cluster of tabs that each stack
 * an icon over a label. The active tab carries a soft orange wash + a stubby
 * orange underline. Vertical hairlines separate inactive tabs so the cluster
 * reads as one continuous control.
 */
export default function MailerLayout() {
  const location = useLocation();
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header band.
          - Light: white surface with a soft amber gradient hairline up top.
          - Dark : near-black canvas with the same amber hairline + an
                   ambient orange radial bleeding from the left edge. */}
      <div className="relative border-b hairline bg-white dark:bg-neutral-950 dark:border-neutral-800 sticky top-0 z-30 overflow-hidden">
        {/* Top accent line — visible in both themes. */}
        <div
          aria-hidden
          className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[hsl(var(--kf-gold))]/80 to-transparent dark:via-[hsl(var(--kf-gold))]"
        />
        {/* Soft ambient glow (dark mode only) bleeding from the left so the
            medallion area glints subtly — mirrors the reference image. */}
        <div
          aria-hidden
          className="hidden dark:block absolute -left-32 -top-24 h-[260px] w-[420px] rounded-full bg-[hsl(var(--kf-gold))]/[0.18] blur-3xl pointer-events-none"
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-4">
          <div className="flex items-center gap-6">
            {/* Brand block — medallion + title. */}
            <div className="flex items-center gap-3 shrink-0">
              <div className="relative">
                <div className="h-10 w-10 rounded-full bg-neutral-900 text-white dark:bg-neutral-900 dark:ring-1 dark:ring-white/[0.06] flex items-center justify-center shadow-[0_4px_14px_-4px_rgba(15,22,38,0.4)]">
                  <Send className="h-4 w-4" strokeWidth={2.2} />
                </div>
                {/* Pulsing gold indicator dot, top-right of the medallion */}
                <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[hsl(var(--kf-gold))] ring-2 ring-white dark:ring-neutral-950" />
              </div>
              <h1 className="text-[20px] font-bold tracking-tight text-neutral-900 dark:text-white leading-none whitespace-nowrap">
                Mailer Dashboard
              </h1>
            </div>

            {/* Tab cluster — stacked icon/label tabs, centered in the row.
                A soft amber wash + bottom underline marks the active tab;
                inactive tabs are joined by a thin vertical hairline. */}
            <nav
              data-testid="mailer-subnav"
              className="mx-auto inline-flex items-stretch rounded-2xl bg-neutral-50/60 dark:bg-white/[0.02] border hairline dark:border-white/[0.06] px-1 py-1"
            >
              {SUB_NAV.map((n, i) => {
                const Icon = n.icon;
                const active = n.end ? location.pathname === n.to : location.pathname.startsWith(n.to);
                return (
                  <React.Fragment key={n.to}>
                    {i > 0 && (
                      <span
                        aria-hidden
                        className="self-center h-7 w-px bg-neutral-200/80 dark:bg-white/[0.06]"
                      />
                    )}
                    <NavLink
                      to={n.to}
                      end={n.end}
                      data-testid={`mailer-tab-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        'relative inline-flex flex-col items-center justify-center min-w-[88px] px-3.5 py-1.5 rounded-xl transition-colors',
                        active
                          ? 'bg-[hsl(var(--kf-gold))]/[0.10] dark:bg-[hsl(var(--kf-gold))]/[0.14]'
                          : 'hover:bg-neutral-100/70 dark:hover:bg-white/[0.04]',
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-[18px] w-[18px] mb-1 transition-colors',
                          active
                            ? 'text-[hsl(var(--kf-gold-deep))] dark:text-[hsl(var(--kf-gold))]'
                            : 'text-neutral-500 dark:text-neutral-300',
                        )}
                        strokeWidth={2}
                      />
                      <span
                        className={cn(
                          'text-[12px] font-semibold leading-none transition-colors',
                          active
                            ? 'text-[hsl(var(--kf-gold-deep))] dark:text-[hsl(var(--kf-gold))]'
                            : 'text-neutral-600 dark:text-neutral-300',
                        )}
                      >
                        {n.label}
                      </span>
                      {/* Stubby orange underline on the active tab */}
                      <span
                        aria-hidden
                        className={cn(
                          'absolute -bottom-1 left-1/2 -translate-x-1/2 h-[2.5px] w-6 rounded-full transition-opacity',
                          active ? 'bg-[hsl(var(--kf-gold))] opacity-100' : 'opacity-0',
                        )}
                      />
                    </NavLink>
                  </React.Fragment>
                );
              })}
            </nav>

            {/* Right-side spacer — keeps the brand-block and tab-cluster
                visually balanced (brand is fixed-width on the left, tabs
                hug the center via `mx-auto`, this empty div anchors the
                right edge so the cluster doesn't slide). */}
            <div className="shrink-0 w-[260px]" aria-hidden />
          </div>
        </div>
      </div>

      <div className="flex-1 bg-neutral-50/40 dark:bg-neutral-950">
        <Outlet />
      </div>
    </div>
  );
}
